/**
 * Internal Labor Rates (PR4)
 * ──────────────────────────
 * Loaded role rates (wage + burden + overhead) used to convert phase hours
 * into estimated labor cost for INTERNAL CREW jobs.
 *
 * Source of truth (beta): static table. Future: per-org override via a
 * settings table. Resolver consumers MUST go through `getLoadedRate()`
 * — never inline rates.
 *
 * "Loaded" = base wage + payroll burden (FICA, WC, UI) + a fixed overhead
 * allocation. Tuned to be conservative for variance reporting.
 *
 * NOTE: This table is internal-only. NEVER expose loaded rates to
 * external contractors or installer-facing surfaces.
 */

export type InternalCrewRole =
  | "crew_lead"
  | "installer"
  | "apprentice"
  | "plumber"
  | "electrician"
  | "tile_setter"
  | "helper";

export interface LoadedRoleRate {
  role: InternalCrewRole;
  /** Hourly dollars, fully loaded. */
  loadedRate: number;
  label: string;
}

/** Beta defaults. Florida market, 2026. */
const DEFAULT_RATES: Record<InternalCrewRole, LoadedRoleRate> = {
  crew_lead:    { role: "crew_lead",    loadedRate: 68, label: "Crew Lead" },
  installer:    { role: "installer",    loadedRate: 54, label: "Installer" },
  apprentice:   { role: "apprentice",   loadedRate: 36, label: "Apprentice" },
  plumber:      { role: "plumber",      loadedRate: 82, label: "Plumber" },
  electrician:  { role: "electrician",  loadedRate: 84, label: "Electrician" },
  tile_setter:  { role: "tile_setter",  loadedRate: 62, label: "Tile Setter" },
  helper:       { role: "helper",       loadedRate: 28, label: "Helper" },
};

export function getLoadedRate(role: InternalCrewRole): number {
  return DEFAULT_RATES[role]?.loadedRate ?? 0;
}

export function listLoadedRates(): LoadedRoleRate[] {
  return Object.values(DEFAULT_RATES);
}

export function isInternalCrewRole(value: string): value is InternalCrewRole {
  return value in DEFAULT_RATES;
}
